import { useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import { PencilIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import MarkdownEditorBlockNote from '@/components/atoms/MarkdownEditor/MarkdownEditorBlockNote';
import { Button } from '@/components/ui/button';
import { useSendCorrection } from '@/hooks/useSendCorrection';
import { cn } from '@/lib/utils';
import { AmendPayload } from '@/shared/types/tryIt';
import { delay, formatErrorMessage } from '@/util/util';

import { PlainRow } from './DetailsRow';
import { submitCorrection } from '../../../redux/actions';

interface Props {
  children?: React.ReactNode;
  footer?: React.ReactNode;
  isAmended: boolean;
  amendPayload: AmendPayload;
  correctionText?: string;
  originalText?: string;
  flat?: boolean;
}

export const INLINE_TRIGGER_CLASS =
  'ml-2 inline leading-5 [&_svg]:mr-1 [&_svg]:inline-block [&_svg]:align-text-top';

export const AMEND_BUTTON_CLASS =
  'flex items-center justify-center gap-1 rounded-sm p-0 text-sm leading-none font-medium text-surface-button-primary transition hover:text-surface-button-primary/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:text-current';

export const AmendedLabel = ({ onEdit }: { onEdit?: () => void }) => {
  const { t } = useTranslation();

  return (
    <span className="inline leading-5">
      <span className="text-sm leading-none font-medium text-text-success-firm">
        {t('try_it.amended')}
      </span>
      {onEdit && (
        <>
          <span className="ml-1 text-xs leading-4 font-normal text-foreground-muted">·</span>
          <button
            type="button"
            onClick={onEdit}
            className={cn(AMEND_BUTTON_CLASS, INLINE_TRIGGER_CLASS, 'ml-1')}
          >
            <PencilIcon size={16} />
            {t('common.edit')}
          </button>
        </>
      )}
    </span>
  );
};

const AmendLine = ({
  flat,
  footer,
  control,
}: {
  flat: boolean;
  footer?: React.ReactNode;
  control: React.ReactNode;
}) =>
  flat ? (
    <div className="flex flex-col items-start gap-4 pb-2">
      <PlainRow>{footer}</PlainRow>
      {control}
    </div>
  ) : (
    <div className="mt-1">
      <PlainRow>
        {footer}
        {control}
      </PlainRow>
    </div>
  );

interface Form {
  correction: string;
}

const MAX_FORM_CHARACTERS = 1500;

const schema = (t: TFunction) =>
  yup
    .object({
      correction: yup
        .string()
        .max(MAX_FORM_CHARACTERS, t('validation.max_length', { 0: MAX_FORM_CHARACTERS }))
        .required(t('validation.required')),
    })
    .required();

export const AmendWrapper = ({
  children = <></>,
  footer,
  amendPayload,
  isAmended,
  correctionText,
  originalText,
  flat = false,
}: Props) => {
  const dispatch = useDispatch();
  const detailsRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { sendCorrection, isPending: isAmending } = useSendCorrection();
  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({ resolver: yupResolver(schema(t)) as Resolver<Form> });
  const correction = useWatch({ control, name: 'correction' });

  const [show, setShow] = useState(false);
  const [amended, setAmended] = useState(isAmended);

  const onSubmit = async (data: Form) => {
    if (isAmending) {
      return null;
    }
    const payload = { ...amendPayload, correction: data.correction };

    try {
      const result = await sendCorrection(payload);
      if (result.status == 'ok') {
        dispatch(submitCorrection(payload));
        toast.success(t('try_it.amendment_submitted'));
        setShow(false);
        await delay(300);
        setAmended(true);
      }
    } catch {
      // useSendCorrection surfaces the error via toast; swallow here to keep UI state consistent.
    }
  };

  const handleShowClick = () => {
    if (!show) {
      setValue('correction', correctionText ?? originalText ?? '');
      setShow(true);
      requestAnimationFrame(() => {
        const editable = detailsRef.current?.querySelector<HTMLElement>('[contenteditable="true"]');
        if (editable) {
          editable.focus();
          editable.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  };

  const handleEdit = () => {
    setValue('correction', correctionText ?? '');
    setAmended(false);
    handleShowClick();
  };

  if (amended) {
    return (
      <div>
        {children}
        <AmendLine
          flat={flat}
          footer={footer}
          control={
            <span className={cn(!flat && 'ml-2')}>
              <AmendedLabel onEdit={handleEdit} />
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div>
      {children}
      <div>
        <AmendLine
          flat={flat}
          footer={footer}
          control={
            !show && (
              <button
                type="button"
                onClick={handleShowClick}
                className={cn(AMEND_BUTTON_CLASS, !flat && INLINE_TRIGGER_CLASS)}
              >
                <PencilIcon size={16} />
                {t('try_it.amend')}
              </button>
            )
          }
        />

        <div
          data-amend-open={show}
          className={cn(
            'grid transition-all duration-200 ease-in-out',
            show ? 'mt-4 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div
            className={cn(
              'flex flex-col items-start gap-3 self-stretch',
              show ? 'overflow-visible py-2' : 'overflow-hidden'
            )}
            ref={detailsRef}
          >
            <div className="flex flex-col items-start gap-2 self-stretch p-0">
              <div className="self-stretch rounded-md bg-background">
                <MarkdownEditorBlockNote<Form>
                  name="correction"
                  setValue={setValue}
                  className="resize-y overflow-auto"
                  error={!!errors.correction}
                  value={correction}
                />
              </div>

              <div className="flex flex-col gap-1.5 self-stretch">
                <span className="self-stretch text-sm leading-5 font-normal text-foreground-muted">
                  {t('try_it.upon_approval')}
                </span>

                {errors.correction && (
                  <p className="self-stretch text-sm leading-5 font-normal text-warning">
                    {formatErrorMessage(errors.correction)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 self-stretch">
              <Button variant="ghost-neutral" size="sm" onClick={() => setShow(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" size="sm" onClick={handleSubmit(onSubmit)}>
                {t('common.submit')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
